import React, { useEffect, useMemo, useState } from "react";
import { Modal, Input, message } from "antd";
import { MessageSquare, Send, AlertTriangle } from "lucide-react";
import { whatsappApi } from "../services/api";
import { normalizeWhatsAppPhone } from "../utils/helpers";

// ============================================================
// 💬 Loan WhatsApp Modal — loan ke ready-made messages bhejne ke liye
// ------------------------------------------------------------
// Backend auto-send (LoanApplicationService.java) alag kaam hai.
// Ye frontend se MANUAL send hai: agent template chunta hai,
// text edit kar sakta hai, phir bhejta hai.
//
// Rule: koi bhi {placeholder} khali reh gaya to send block hoga —
// customer ko "undefined" ya adhoora message kabhi na jaaye.
// ============================================================

const { TextArea } = Input;

const formatINR = (val) => {
  if (val === undefined || val === null || val === "" || isNaN(val)) return "";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(val));
};

// ── Paanch templates (spec ke hisaab se) ──
export const LOAN_WA_TEMPLATES = {
  APPLICATION_SUBMITTED: {
    label: "Application Submitted",
    build: (c) =>
      `Dear ${c.name || "{name}"}, your loan application ${c.applicationId || "{applicationId}"} for ${formatINR(c.amount) || "{amount}"} has been submitted successfully. Our team will review within 24-48 hours.`,
  },
  LOAN_APPROVED: {
    label: "Loan Approved",
    build: (c) =>
      `Congratulations! Your loan of ${formatINR(c.amount) || "{amount}"} has been APPROVED. Account: ${c.accountNumber || "{accountNumber}"}. EMI: ${formatINR(c.emiAmount) || "{emiAmount}"}/month starting ${c.firstEmiDate || "{firstEmiDate}"}.`,
  },
  LOAN_REJECTED: {
    label: "Loan Rejected",
    build: (c) =>
      `Dear ${c.name || "{name}"}, your loan application ${c.applicationId || "{applicationId}"} has been rejected. Reason: ${c.reason || "{reason}"}. Contact us for assistance.`,
  },
  EMI_REMINDER: {
    label: "EMI Reminder",
    build: (c) =>
      `Dear ${c.name || "{name}"}, EMI of ${formatINR(c.emiAmount) || "{amount}"} due on ${c.dueDate || "{date}"}. Loan A/C: ${c.accountNumber || "{accountNumber}"}. Pay online: ${c.paymentLink || "{paymentLink}"}`,
  },
  EMI_OVERDUE: {
    label: "EMI Overdue",
    build: (c) =>
      `URGENT: EMI of ${formatINR(c.emiAmount) || "{amount}"} for loan ${c.accountNumber || "{accountNumber}"} is OVERDUE since ${c.overdueSince || "{date}"}. Pay immediately to avoid penalty.`,
  },
};

const LoanWhatsAppModal = ({
  open,
  onClose,
  phone,
  templateKeys = [],
  context = {},
  defaultTemplate,
}) => {
  const available = useMemo(
    () => templateKeys.filter((k) => LOAN_WA_TEMPLATES[k]),
    [templateKeys],
  );

  const [selected, setSelected] = useState(defaultTemplate || available[0]);
  const [text, setText] = useState("");
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);

  // Modal khulte hi template + phone reset karo
  useEffect(() => {
    if (!open) return;
    const key = defaultTemplate && LOAN_WA_TEMPLATES[defaultTemplate]
      ? defaultTemplate
      : available[0];
    setSelected(key);
    setText(key ? LOAN_WA_TEMPLATES[key].build(context) : "");
    setTo(phone ? String(phone) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTemplate, phone]);

  const pickTemplate = (key) => {
    setSelected(key);
    setText(LOAN_WA_TEMPLATES[key].build(context));
  };

  // Jo data nahi mila wo {placeholder} ban ke dikhta hai
  const missing = text.match(/\{[a-zA-Z]+\}/g) || [];
  const digits = normalizeWhatsAppPhone(to);
  const phoneOk = digits.length >= 12;

  const handleSend = async () => {
    if (missing.length > 0) {
      message.warning(`Pehle ye fill karo: ${missing.join(", ")}`);
      return;
    }
    if (!phoneOk) {
      message.warning("Valid WhatsApp number daalo (10 digit)");
      return;
    }
    setSending(true);
    try {
      await whatsappApi.sendText(digits, text.trim());
      message.success("WhatsApp message bhej diya gaya");
      onClose?.();
    } catch (err) {
      console.error("Loan WhatsApp send failed:", err);
      message.error(err?.message || "Message nahi bheja ja saka");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={620}
      destroyOnHidden
      title={
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-[#2e856e]" />
          <span className="font-bold">Send WhatsApp</span>
        </div>
      }
    >
      <div className="space-y-4 pt-2">
        {/* Template chooser */}
        <div>
          <p className="text-xs font-bold uppercase text-gray-400 mb-2">
            Template
          </p>
          <div className="flex flex-wrap gap-2">
            {available.map((key) => (
              <button
                key={key}
                onClick={() => pickTemplate(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                  selected === key
                    ? "bg-[#2e856e] text-white border-[#2e856e]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-[#2e856e]"
                }`}
              >
                {LOAN_WA_TEMPLATES[key].label}
              </button>
            ))}
          </div>
        </div>

        {/* Number */}
        <div>
          <p className="text-xs font-bold uppercase text-gray-400 mb-2">
            WhatsApp Number
          </p>
          <Input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="10 digit mobile number"
            status={to && !phoneOk ? "error" : ""}
          />
        </div>

        {/* Message — agent edit kar sakta hai */}
        <div>
          <p className="text-xs font-bold uppercase text-gray-400 mb-2">
            Message
          </p>
          <TextArea
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        {/* Adhoora data — send block */}
        {missing.length > 0 && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700 font-medium">
              Ye details system me nahi mili: {missing.join(", ")}. Message me
              inhe khud bhar do, tabhi send hoga.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-bold text-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || missing.length > 0 || !phoneOk || !text.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#2e856e] text-white text-sm font-bold disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LoanWhatsAppModal;
